import { z } from 'zod';

/**
 * Catálogos: las listas que alimentan los campos de selección.
 *
 * ── Uno solo, no uno por lista ──
 * Áreas, cargos y sectores viven en la MISMA tabla, distinguidos por `tipo`.
 * Agregar una lista nueva cuesta una entrada en `CATALOGOS` y nada de interfaz.
 */

export const TIPOS_CATALOGO = ['sector', 'area', 'cargo', 'competencia', 'programa'] as const;
export type TipoCatalogo = (typeof TIPOS_CATALOGO)[number];

/**
 * ── La diferencia que hay que tener clara ──
 * El **sector** es global: «Manufactura» es la misma para todos los clientes, y
 * cuelga de la fila de Copathy. El **área** y el **cargo** son de cada cliente:
 * los de Seguros El Ávila no tienen por qué parecerse a los de otro, y mezclarlos
 * sería que un cliente vea la estructura interna de otro — la frontera 1.
 */
export const CATALOGOS: {
  tipo: TipoCatalogo;
  titulo: string;
  singular: string;
  ayuda: string;
  /** `true` = una sola lista para toda la plataforma; `false` = una por cliente. */
  global: boolean;
}[] = [
  {
    tipo: 'sector',
    titulo: 'Sectores',
    singular: 'sector',
    ayuda: 'En qué rubro opera cada cliente. Es la misma lista para todos.',
    global: true,
  },
  {
    tipo: 'area',
    titulo: 'Áreas',
    singular: 'área',
    ayuda:
      'Los departamentos de un cliente. Sirven para agrupar resultados sin identificar a nadie.',
    global: false,
  },
  {
    tipo: 'cargo',
    titulo: 'Cargos',
    singular: 'cargo',
    ayuda: 'Los puestos de un cliente. Cada cliente tiene los suyos.',
    global: false,
  },
  /*
    La competencia es lo que una pregunta MIDE —liderazgo, comunicación,
    negociación—, y es global porque el marco es de Copathy: si cada cliente
    tuviera el suyo, dos mediciones de «liderazgo» no serían comparables y el
    «antes vs. después» entre clientes dejaría de existir.

    ⚠️ Vive acá y no en una tabla propia **a propósito**: M4 —programas— todavía
    no está construido, y adivinar hoy la forma de su tabla es diseñar a ciegas.
    Cuando M4 llegue, la competencia se muda y `preguntas.competencia_id` cambia
    de FK. Está anotado en la 011 para que no aparezca de sorpresa.
  */
  {
    tipo: 'competencia',
    titulo: 'Competencias',
    singular: 'competencia',
    ayuda:
      'Qué habilidad mide cada pregunta. Es lo que permite agrupar resultados por competencia.',
    global: true,
  },
  /*
    Los programas que Copathy vende. Llegaron con la migración 014, tal como
    estaban en AppSheet.

    ⚠️ Todavía **no cuelga nada de ellos**: son una lista para tener a mano
    mientras M4 no exista. El grupo comercial de cada uno —Liderazgo, Desarrollo
    de Equipos, Servicio al Cliente, Medición de Desempeño— no se guarda acá; está
    en `docs/10-appsheet-antecedente.md` esperando a M4.
  */
  {
    tipo: 'programa',
    titulo: 'Programas',
    singular: 'programa',
    ayuda: 'La oferta de COPATHY. Todavía no se le asigna nada: es la lista de referencia.',
    global: true,
  },
];

export const definicionCatalogo = (tipo: TipoCatalogo) =>
  CATALOGOS.find((c) => c.tipo === tipo)!;

export const esquemaOpcionCatalogo = z.object({
  id: z.number().int().positive(),
  tipo: z.enum(TIPOS_CATALOGO),
  nombre: z.string(),
  empresaId: z.number().int().positive(),
  activo: z.boolean(),
  /** Cuántos registros la están usando. Es lo que decide si se puede desactivar sin romper nada. */
  enUso: z.number().int(),
});
export type OpcionCatalogo = z.infer<typeof esquemaOpcionCatalogo>;

/**
 * ⚠️ El nombre se normaliza **igual que `ux_catalogos_empresa_tipo_nombre`**
 * —sin espacios en los bordes, sin distinguir mayúsculas—. Si acá se normalizara
 * distinto, el índice dejaría pasar duplicados y la lista terminaría con
 * «Ventas», «ventas» y «Ventas » como tres opciones.
 */
export const esquemaNombreOpcion = z
  .string()
  .trim()
  .min(2, 'El nombre es demasiado corto.')
  .max(120, 'El nombre es demasiado largo.');

export const esquemaCrearOpcion = z.object({
  tipo: z.enum(TIPOS_CATALOGO),
  nombre: esquemaNombreOpcion,
  /**
   * A qué cliente pertenece. **Se ignora para los catálogos globales**: el sector
   * siempre cuelga de Copathy, lo mande quien lo mande. Aceptarlo ahí crearía
   * catálogos de sector por cliente sin que nadie se diera cuenta.
   */
  empresaId: z.number().int().positive().optional(),
});
export type EntradaCrearOpcion = z.infer<typeof esquemaCrearOpcion>;

export const esquemaEditarOpcion = z
  .object({
    nombre: esquemaNombreOpcion.optional(),
    activo: z.boolean().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, 'No enviaste ningún cambio.');
export type EntradaEditarOpcion = z.infer<typeof esquemaEditarOpcion>;

export const esquemaFiltroCatalogo = z.object({
  tipo: z.enum(TIPOS_CATALOGO),
  empresaId: z.coerce.number().int().positive().optional(),
  /** `si` (por defecto) · `no` · `todas`. En administración se ven las inactivas. */
  activo: z
    .enum(['si', 'no', 'todas'])
    .default('si')
    .transform((v) => (v === 'todas' ? undefined : v === 'si')),
});
export type FiltroCatalogo = z.infer<typeof esquemaFiltroCatalogo>;
